import type React from 'react';

export enum CardStyle {
  Elevated = 'Elevated',
  Outlined = 'Outlined',
  Filled = 'Filled',
}

// Card Container Props
export interface CardProps {
  /**
   * Card style variant: Elevated, Outlined, or Filled
   */
  style?: CardStyle;
  /**
   * Whether the card is clickable
   */
  clickable?: boolean;
  /**
   * Click handler for the entire card
   */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Card width (default: 360px)
   */
  width?: number | string;
  /**
   * Card children (CardHeader, CardBody, CardFooter)
   */
  children?: React.ReactNode;
}

// Card Header Props
export interface CardHeaderProps {
  /**
   * Avatar content (can be an image, icon, or text)
   */
  avatar?: React.ReactNode;
  /**
   * Header/title text
   */
  title?: string;
  /**
   * Subhead/subtitle text
   */
  subtitle?: string;
  /**
   * Whether to show the menu icon (3-dot)
   */
  showMenu?: boolean;
  /**
   * Handler for menu icon click
   */
  onMenuClick?: (e: React.MouseEvent) => void;
  /**
   * Custom action element (replaces menu icon)
   */
  action?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Children for fully custom header content
   */
  children?: React.ReactNode;
}

// Card Body Props
export interface CardBodyProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Padding size
   */
  padding?: 'none' | 'small' | 'medium' | 'large';
  /**
   * Body content
   */
  children?: React.ReactNode;
}

// Card Media Props (for use inside CardBody)
export interface CardMediaProps {
  /**
   * Image source URL
   */
  src?: string;
  /**
   * Alt text for the image
   */
  alt?: string;
  /**
   * Custom content to render in media area
   */
  children?: React.ReactNode;
  /**
   * Height of the media area (default: 194px)
   */
  height?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
}

// Card Content Props (for use inside CardBody)
export interface CardContentProps {
  /**
   * Title text
   */
  title?: string;
  /**
   * Subtitle text
   */
  subtitle?: string;
  /**
   * Description/body text or custom ReactNode for styled content
   */
  description?: React.ReactNode;
  /**
   * Additional CSS classes for the description element
   */
  descriptionClassName?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Custom content to render
   */
  children?: React.ReactNode;
}

// Card Footer Props
export interface CardFooterProps {
  /**
   * Alignment of footer content
   */
  align?: 'left' | 'center' | 'right' | 'space-between';
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Footer content (typically buttons)
   */
  children?: React.ReactNode;
}

// Card Action Button Props
export interface CardActionProps {
  /**
   * Button label
   */
  label: string;
  /**
   * Button variant: 'primary' or 'secondary'
   */
  variant?: 'primary' | 'secondary';
  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}
